import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { MaskService } from '@shared/services/mask.service';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { CstCofinsStruct } from '@shared/services/structs/motor-fiscal/cst-cofins.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { getCstPisCofinsTexto, CstPisCofinsTexto } from '@shared/constants/motor-fiscal/cst-pis-cofins-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    isAlgumCampoAtivo,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';

/** Campo do indicador do CST do COFINS -> control da regra da natureza (aba e wizard de exceção usam os mesmos nomes). */
export const MAPA_CAMPOS_COFINS_NATUREZA: MapaCamposIndicador = {
    pCOFINS: 'pCofins',
    vAliqProd: 'vAliqProd',
    pCOFINSST: 'pCofinsSt',
    vAliqProdST: 'vAliqProdSt',
};

@Component({
    selector: 'app-cofins-tributo-regra-section',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatBadgeModule,
        MatIconModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        AlertCardComponent,
    ],
    templateUrl: './tributo-regra-section.component.html',
    styleUrl: './tributo-regra-section.component.css',
})
export class CofinsTributoRegraSectionComponent implements OnChanges {
    @Input({ required: true }) form!: FormGroup;
    @Input() codigoTributos: CstCofinsStruct[] = [];
    @Input() variaveis: VariavelStruct[] = [];
    @Input() personalizadasCount = 0;
    @Input() modoExcecao = false;
    @Input() personalizarDisabled = false;
    /** Tipo de operação da natureza: Entrada ou Saída. Define a faixa de CST permitida. */
    @Input() tipoOperacao: TipoOperacaoEnum = TipoOperacaoEnum.Saida;
    @Output() personalizar = new EventEmitter<void>();

    public readonly percentMask = this.maskService.getPercentMask();
    public readonly currencyMask = this.maskService.getCurrencyMask();

    /** Só os CSTs da direção (Entrada/Saída) da natureza podem ser escolhidos. */
    public get codigoTributosDoTipoOperacao(): CstCofinsStruct[] {
        return this.codigoTributos.filter(c => isCodigoDaOperacao(c.indicadores, this.tipoOperacao));
    }

    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];

    constructor(
        private maskService: MaskService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['form'] && this.form) {
            // Zera os campos que o novo CST não exibe (evita valor obsoleto no payload).
            this.form.get('cstCofins')?.valueChanges.subscribe(() =>
                limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_COFINS_NATUREZA));
        }

        // Trocar o tipo de operação troca a direção de CST permitida: um código da
        // direção anterior deixaria de aparecer no select e não pode continuar selecionado.
        if (changes['tipoOperacao'] && !changes['tipoOperacao'].firstChange && this.form) {
            const control = this.form.get('cstCofins');
            const indicadores = this.codigoTributos.find(c => c.codigo === control?.value)?.indicadores;
            if (control?.value && !isCodigoDaOperacao(indicadores, this.tipoOperacao)) {
                control.setValue(null);
            }
        }
    }

    public get indicadores(): IndicadoresCst {
        const cst = this.form?.get('cstCofins')?.value ?? null;
        return lerIndicadoresCst(this.codigoTributos.find(c => c.codigo === cst)?.indicadores);
    }

    public get texto(): CstPisCofinsTexto | null {
        const cst = this.form?.get('cstCofins')?.value ?? null;
        return getCstPisCofinsTexto(cst, this.codigoTributos.find(c => c.codigo === cst)?.descricao, this.indicadores, 'COFINS');
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    public algumAtivo(chaves: string[]): boolean {
        return isAlgumCampoAtivo(this.indicadores, chaves);
    }

    /** True quando o botão "Personalizar fórmulas" não pode ser acessado. */
    public get personalizarBloqueado(): boolean {
        return this.personalizarDisabled || !this.form?.get('cstCofins')?.value;
    }

    /** Mensagem do tooltip exibida apenas quando o botão está desabilitado. */
    public get personalizarTooltip(): string {
        if (this.personalizarDisabled) {
            return this.modoExcecao
                ? 'A personalização de fórmulas não está disponível para exceções.'
                : 'A personalização de fórmulas está indisponível no momento.';
        }
        if (!this.form?.get('cstCofins')?.value) {
            return 'Selecione um Código para habilitar a personalização de fórmulas.';
        }
        return '';
    }

    public onPersonalizar(): void {
        this.personalizar.emit();
    }
}
